import { DASHBOARD_CARDS } from '../../../../../common/dashboard-cards.js'
import type { FilterScope } from '../../utils/portal-api.js'

export interface LayoutCard {
    key: string
    colSpan: number
    rowSpan: number
}

/** Ordered bento layout: a single list of cards, each with its grid spans. */
export type Layout = LayoutCard[]

/**
 * Pure equality check for two dashboard layouts. Two layouts are equal when
 * they have the same length and the same cards (key + spans) in the same order.
 */
export const layoutsEqual = (a: Layout, b: Layout): boolean =>
    a.length === b.length &&
    a.every((c, i) => {
        const o = b[i]
        return c.key === o?.key && c.colSpan === o?.colSpan && c.rowSpan === o?.rowSpan
    })

/**
 * `true` when the user has unsaved edits to their layout draft.
 */
export const isDirty = (saved: Layout | null, draft: Layout | null): boolean => {
    if (!saved || !draft) return false
    return !layoutsEqual(saved, draft)
}

/**
 * Build the GET URL for the /payload endpoint given the visible layout and
 * an optional filter scope. Returns an empty string when the layout has no
 * cards (caller should skip the fetch in that case).
 */
export const buildPayloadUrl = (layout: Layout, scope: FilterScope | undefined): string => {
    const allKeys = layout.map((c) => c.key)
    if (allKeys.length === 0) return ''
    const params = new URLSearchParams()
    params.set('cards', allKeys.join(','))
    if (scope?.company_id) params.set('company_id', String(scope.company_id))
    if (scope?.team_id) params.set('team_id', String(scope.team_id))
    return `/api/portal/dashboard/payload?${params.toString()}`
}

/**
 * Split the visible layout into independent /payload fetch batches: all fast
 * (DB-backed) cards in one batch, plus each slow-resolver card (live external
 * API — see the registry's `slowResolver` flag) alone. Fetching the batches in
 * parallel lets the fast cards render immediately instead of every card
 * waiting on the slowest external resolver. Unknown keys count as fast.
 */
export const buildPayloadBatches = (layout: Layout): Layout[] => {
    const isSlow = (key: string): boolean =>
        (DASHBOARD_CARDS as Record<string, { slowResolver?: boolean } | undefined>)[key]
            ?.slowResolver === true
    const fast = layout.filter((c) => !isSlow(c.key))
    const slow = layout.filter((c) => isSlow(c.key))
    return [...(fast.length > 0 ? [fast] : []), ...slow.map((c) => [c])]
}
